import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import api from '../lib/api';
import { h, writePrintHtml } from '../lib/printHelpers';

const DistributorDispatch = ({ user }) => {
  const [distributors, setDistributors] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [turnoverData, setTurnoverData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [viewingDispatch, setViewingDispatch] = useState(null);
  const [activeTab, setActiveTab] = useState('dispatches');

  // Create form
  const [selectedDistributor, setSelectedDistributor] = useState('');
  const [dispatchDate, setDispatchDate] = useState(new Date().toISOString().split('T')[0]);
  const [transportName, setTransportName] = useState('');
  const [dispatchNotes, setDispatchNotes] = useState('');
  const [pendingData, setPendingData] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [filterDistributor, setFilterDistributor] = useState('');

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const canCreate = ['admin', 'manager', 'inventory_staff'].includes(user?.role);

  const loadData = async () => {
    try {
      const [distRes, dispRes] = await Promise.all([
        api.get('/distributors'), api.get('/distributor-dispatch'),
      ]);
      setDistributors(distRes.data);
      setDispatches(dispRes.data);
    } catch (err) { toast.error('Failed to load data'); }
    setLoading(false);
  };

  const loadTurnover = async (distId) => {
    try {
      const params = distId ? `?distributor_id=${distId}` : '';
      const res = await api.get(`/distributor-dispatch/analytics/customer-turnover${params}`);
      setTurnoverData(res.data);
    } catch (err) { toast.error('Failed to load turnover data'); }
  };

  useEffect(() => { loadData(); }, []);
  useEffect(() => { if (activeTab === 'turnover') loadTurnover(filterDistributor); }, [activeTab, filterDistributor]);

  // Load pending orders when distributor selected
  useEffect(() => {
    if (selectedDistributor) {
      api.get(`/distributor-dispatch/pending/${selectedDistributor}`)
        .then(res => { setPendingData(res.data); setSelectedOrders([]); })
        .catch(() => setPendingData(null));
    } else { setPendingData(null); setSelectedOrders([]); }
  }, [selectedDistributor]);

  const downloadTurnover = async (format) => {
    try {
      const params = filterDistributor ? `?distributor_id=${filterDistributor}` : '';
      const res = await api.get(`/distributor-dispatch/analytics/customer-turnover/export/${format}${params}`, { responseType: 'blob' });
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `customer_turnover.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Turnover report downloaded as ${ext.toUpperCase()}`);
    } catch (err) { toast.error(`Failed to download ${format}`); }
  };

  const distMap = Object.fromEntries(distributors.map(d => [d.id, d]));
  const filteredDispatches = filterDistributor ? dispatches.filter(d => d.distributor_id === filterDistributor) : dispatches;

  const toggleOrder = (soId) => {
    setSelectedOrders(prev => prev.includes(soId) ? prev.filter(id => id !== soId) : [...prev, soId]);
  };
  const selectAllOrders = () => {
    if (!pendingData) return;
    const allIds = pendingData.orders.map(o => o.id);
    setSelectedOrders(prev => prev.length === allIds.length ? [] : allIds);
  };

  const selectedOrdersSummary = () => {
    if (!pendingData) return { qty: 0, value: 0, customers: 0 };
    const selected = pendingData.orders.filter(o => selectedOrders.includes(o.id));
    const custIds = new Set(selected.map(o => o.customer_id));
    return {
      qty: selected.reduce((s, o) => s + (o.total_qty || 0), 0),
      value: selected.reduce((s, o) => s + (o.total_amount || 0), 0),
      customers: custIds.size,
    };
  };

  const resetForm = () => {
    setSelectedDistributor(''); setDispatchDate(new Date().toISOString().split('T')[0]);
    setTransportName(''); setDispatchNotes(''); setPendingData(null); setSelectedOrders([]);
  };

  const submitDispatch = async () => {
    if (!selectedDistributor) { toast.error('Select a distributor'); return; }
    if (selectedOrders.length === 0) { toast.error('Select at least one order'); return; }
    try {
      const res = await api.post('/distributor-dispatch', {
        distributor_id: selectedDistributor,
        dispatch_date: dispatchDate,
        transport_name: transportName || null,
        so_ids: selectedOrders,
        notes: dispatchNotes || null,
      });
      toast.success(`Dispatch ${res.data.dispatch_number} created! Invoice raised against ${res.data.distributor_name}`);
      resetForm(); setIsCreateOpen(false); loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create dispatch'); }
  };

  const deleteDispatch = async (id, num) => {
    try {
      await api.delete(`/distributor-dispatch/${id}`);
      toast.success(`${num} deleted. Sales orders reverted.`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const printPackingSlip = async (dispatch, customerId) => {
    try {
      const res = await api.get(`/distributor-dispatch/${dispatch.id}/packing-slip/${customerId}`);
      const slip = res.data;
      const win = window.open('', '_blank');
      if (!win) { toast.error('Please allow popups'); return; }
      writePrintHtml(win, `<!DOCTYPE html><html><head><title>Packing Slip - ${h(slip.customer_name)}</title>
<style>@page{margin:10mm}body{margin:0;font-family:Arial,sans-serif;font-size:12px}
.header{border-bottom:2px solid #000;padding-bottom:8px;margin-bottom:12px}
.header h2{margin:0;font-size:18px}.header .meta{color:#555;font-size:11px}
table{width:100%;border-collapse:collapse;margin-top:8px}th,td{border:1px solid #ccc;padding:6px 8px;text-align:left}
th{background:#f5f5f5;font-weight:bold}.total{font-weight:bold;font-size:14px;margin-top:12px;text-align:right}
@media print{.no-print{display:none}}
</style></head><body>
<div class="no-print" style="padding:8px;text-align:center"><button onclick="window.print()" style="font-size:14px;padding:8px 24px;cursor:pointer">Print</button></div>
<div class="header">
  <h2>PACKING SLIP</h2>
  <div class="meta">Dispatch: ${h(slip.dispatch_number)} | Date: ${h(slip.dispatch_date)} | Transport: ${h(slip.transport || '-')}</div>
  <div class="meta">Distributor: ${h(slip.distributor)}</div>
</div>
<div style="margin-bottom:12px">
  <strong style="font-size:14px">${h(slip.customer_name)}</strong><br/>
  <span class="meta">Phone: ${h(slip.customer_phone || '-')} | City: ${h(slip.customer_city || '-')}</span>
</div>
<table>
  <thead><tr><th>#</th><th>Design Code</th><th>Size</th><th>Color</th><th>Qty</th></tr></thead>
  <tbody>${slip.items.map((item, i) =>
    `<tr><td>${i+1}</td><td>${h(item.design_code)}</td><td>${h(item.size)}</td><td>${h(item.color)}</td><td>${h(item.qty)}</td></tr>`
  ).join('')}</tbody>
</table>
<div class="total">Total Qty: ${h(slip.total_qty)} pcs</div>
</body></html>`);
    } catch (err) { toast.error('Failed to load packing slip'); }
  };

  const printAllSlips = (dispatch) => {
    const slips = dispatch.packing_slips || [];
    if (!slips.length) { toast.info('No packing slips'); return; }
    const win = window.open('', '_blank');
    if (!win) { toast.error('Please allow popups'); return; }
    writePrintHtml(win, `<!DOCTYPE html><html><head><title>All Packing Slips - ${h(dispatch.dispatch_number)}</title>
<style>@page{margin:10mm}body{margin:0;font-family:Arial,sans-serif;font-size:12px}
.slip{page-break-after:always;padding:8px}.slip:last-child{page-break-after:auto}
.header{border-bottom:2px solid #000;padding-bottom:6px;margin-bottom:10px}
.header h2{margin:0;font-size:16px}.meta{color:#555;font-size:11px}
table{width:100%;border-collapse:collapse;margin-top:6px}th,td{border:1px solid #ccc;padding:5px 6px;text-align:left}
th{background:#f5f5f5;font-weight:bold}.total{font-weight:bold;margin-top:8px;text-align:right}
@media print{.no-print{display:none}}
</style></head><body>
<div class="no-print" style="padding:8px;text-align:center"><button onclick="window.print()" style="font-size:14px;padding:8px 24px;cursor:pointer">Print All ${h(slips.length)} Slips</button></div>
${slips.map(slip => `
<div class="slip">
  <div class="header">
    <h2>PACKING SLIP</h2>
    <div class="meta">Dispatch: ${h(dispatch.dispatch_number)} | Date: ${h(dispatch.dispatch_date)} | Transport: ${h(dispatch.transport_name || '-')}</div>
    <div class="meta">Distributor: ${h(dispatch.distributor_name)}</div>
  </div>
  <div style="margin-bottom:8px">
    <strong style="font-size:13px">${h(slip.customer_name)}</strong><br/>
    <span class="meta">Phone: ${h(slip.customer_phone || '-')} | City: ${h(slip.customer_city || '-')}</span>
  </div>
  <table>
    <thead><tr><th>#</th><th>Design</th><th>Size</th><th>Color</th><th>Qty</th></tr></thead>
    <tbody>${slip.items.map((item, i) =>
      `<tr><td>${i+1}</td><td>${h(item.design_code)}</td><td>${h(item.size)}</td><td>${h(item.color)}</td><td>${h(item.qty)}</td></tr>`
    ).join('')}</tbody>
  </table>
  <div class="total">Total: ${h(slip.total_qty)} pcs</div>
</div>`).join('')}
</body></html>`);
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2,'0')}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getFullYear()).slice(-2)}`;
  };
  const summary = selectedOrdersSummary();

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" /></div>;

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="distributor-dispatch-container">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Distributor Dispatch</h1>
          <p className="text-gray-600 mt-1">Consolidate customer orders, generate packing slips, invoice distributors</p>
        </div>
        {canCreate && (
          <Button onClick={() => setIsCreateOpen(true)} className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 shadow-lg" data-testid="create-dispatch-btn">
            + New Dispatch
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="dispatch-tabs">
        <TabsList>
          <TabsTrigger value="dispatches" data-testid="tab-dispatches">Dispatches</TabsTrigger>
          <TabsTrigger value="turnover" data-testid="tab-turnover">Customer Turnover</TabsTrigger>
        </TabsList>

        {/* Dispatches Tab */}
        <TabsContent value="dispatches">
          <Card className="mb-4" data-testid="dispatch-filter-card">
            <CardContent className="p-4">
              <div className="flex gap-3 items-end">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-gray-600">Filter by Distributor</Label>
                  <select value={filterDistributor} onChange={e => setFilterDistributor(e.target.value)}
                    className="flex h-9 w-64 rounded-md border border-input bg-background px-3 py-1 text-sm" data-testid="dispatch-filter-distributor">
                    <option value="">All Distributors</option>
                    {distributors.map(d => <option key={d.id} value={d.id}>{d.company_name} ({d.state || '-'})</option>)}
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="dispatch-list">
            <CardHeader className="pb-3">
              <CardTitle>Dispatches ({filteredDispatches.length})</CardTitle>
              <CardDescription>Consolidated dispatches to distributors with customer-wise packing</CardDescription>
            </CardHeader>
            <CardContent>
              {filteredDispatches.length === 0 ? (
                <div className="text-center py-12">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No Dispatches Yet</h3>
                  <p className="text-gray-600 mb-4">Create your first distributor dispatch to consolidate customer orders.</p>
                  {canCreate && <Button onClick={() => setIsCreateOpen(true)} className="bg-indigo-600">Create First Dispatch</Button>}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">Dispatch #</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">Distributor</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">Date</th>
                        <th className="text-center px-4 py-3 font-semibold text-gray-600">Customers</th>
                        <th className="text-right px-4 py-3 font-semibold text-gray-600">Qty</th>
                        <th className="text-right px-4 py-3 font-semibold text-gray-600">Gross</th>
                        <th className="text-right px-4 py-3 font-semibold text-gray-600">Discount</th>
                        <th className="text-right px-4 py-3 font-semibold text-gray-600">Net Invoice</th>
                        <th className="text-center px-4 py-3 font-semibold text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDispatches.map(d => (
                        <tr key={d.id} className="border-b hover:bg-gray-50/80 transition-colors" data-testid={`dispatch-row-${d.id}`}>
                          <td className="px-4 py-3 font-semibold text-indigo-700">{d.dispatch_number}</td>
                          <td className="px-4 py-3">{d.distributor_name}</td>
                          <td className="px-4 py-3 text-xs">{formatDate(d.dispatch_date)}</td>
                          <td className="px-4 py-3 text-center"><Badge variant="outline">{d.total_customers}</Badge></td>
                          <td className="px-4 py-3 text-right font-medium">{d.total_qty}</td>
                          <td className="px-4 py-3 text-right text-gray-500">₹{(d.gross_value || 0).toLocaleString('en-IN')}</td>
                          <td className="px-4 py-3 text-right text-red-600">-{d.discount_percentage}%</td>
                          <td className="px-4 py-3 text-right font-bold text-emerald-700">₹{(d.net_value || 0).toLocaleString('en-IN')}</td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex gap-1 justify-center">
                              <Button size="sm" variant="ghost" className="h-7 px-2" title="View Details" onClick={() => setViewingDispatch(d)} data-testid={`dispatch-view-${d.id}`}>
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-violet-600" title="Print All Packing Slips" onClick={() => printAllSlips(d)} data-testid={`dispatch-print-${d.id}`}>
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                              </Button>
                              {isAdmin && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:bg-red-50" data-testid={`dispatch-delete-${d.id}`}>
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Delete {d.dispatch_number}?</AlertDialogTitle>
                                      <AlertDialogDescription>Sales orders will revert to confirmed status.</AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => deleteDispatch(d.id, d.dispatch_number)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Customer Turnover Tab */}
        <TabsContent value="turnover">
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="flex gap-3 items-end">
                <div className="space-y-1">
                  <Label className="text-xs font-semibold text-gray-600">Distributor</Label>
                  <select value={filterDistributor} onChange={e => setFilterDistributor(e.target.value)}
                    className="flex h-9 w-64 rounded-md border border-input bg-background px-3 py-1 text-sm" data-testid="turnover-filter-distributor">
                    <option value="">All Distributors</option>
                    {distributors.map(d => <option key={d.id} value={d.id}>{d.company_name}</option>)}
                  </select>
                </div>
                {turnoverData && (
                  <div className="flex gap-4 ml-auto text-sm items-center">
                    <span className="text-gray-600">Customers: <strong>{turnoverData.total_customers}</strong></span>
                    <span className="text-gray-600">Total Turnover: <strong className="text-emerald-700">₹{turnoverData.total_turnover?.toLocaleString('en-IN')}</strong></span>
                    <div className="flex gap-1.5 ml-2">
                      <Button size="sm" variant="outline" className="h-8 text-xs gap-1 text-emerald-700 border-emerald-300 hover:bg-emerald-50" onClick={() => downloadTurnover('excel')} data-testid="turnover-export-excel">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        Excel
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 text-xs gap-1 text-red-700 border-red-300 hover:bg-red-50" onClick={() => downloadTurnover('pdf')} data-testid="turnover-export-pdf">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        PDF
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          <Card data-testid="turnover-table">
            <CardHeader className="pb-3">
              <CardTitle>Customer-wise Turnover</CardTitle>
              <CardDescription>Revenue breakdown by customer across all dispatches — what Tally doesn't show you</CardDescription>
            </CardHeader>
            <CardContent>
              {!turnoverData || turnoverData.customers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">No dispatch data yet. Create dispatches to see customer turnover.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">#</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">Customer</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">City</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-600">Distributor</th>
                        <th className="text-center px-4 py-3 font-semibold text-gray-600">Dispatches</th>
                        <th className="text-right px-4 py-3 font-semibold text-gray-600">Total Qty</th>
                        <th className="text-right px-4 py-3 font-semibold text-gray-600">Turnover</th>
                      </tr>
                    </thead>
                    <tbody>
                      {turnoverData.customers.map((c, i) => (
                        <tr key={c.customer_id} className="border-b hover:bg-gray-50/80">
                          <td className="px-4 py-3 text-gray-400">{i + 1}</td>
                          <td className="px-4 py-3 font-medium">{c.customer_name}</td>
                          <td className="px-4 py-3 text-gray-600">{c.customer_city || '-'}</td>
                          <td className="px-4 py-3 text-gray-600">{c.distributor_name}</td>
                          <td className="px-4 py-3 text-center"><Badge variant="outline">{c.dispatches_count}</Badge></td>
                          <td className="px-4 py-3 text-right">{c.total_qty}</td>
                          <td className="px-4 py-3 text-right font-bold text-emerald-700">₹{c.total_value.toLocaleString('en-IN')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Dispatch Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={v => { if (!v) resetForm(); setIsCreateOpen(v); }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto z-50">
          <DialogHeader>
            <DialogTitle>New Distributor Dispatch</DialogTitle>
            <DialogDescription>Select a distributor, pick customer orders to consolidate, then dispatch with one invoice.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Distributor *</Label>
                <select value={selectedDistributor} onChange={e => setSelectedDistributor(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" data-testid="dispatch-distributor-select">
                  <option value="">Choose Distributor</option>
                  {distributors.filter(d => d.is_active).map(d => (
                    <option key={d.id} value={d.id}>{d.company_name} — {d.state || ''} (Discount: {d.discount_percentage || 0}%)</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Dispatch Date *</Label>
                <Input type="date" value={dispatchDate} onChange={e => setDispatchDate(e.target.value)} className="h-9" data-testid="dispatch-date" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Transport</Label>
                <Input value={transportName} onChange={e => setTransportName(e.target.value)} placeholder="Transport name" className="h-9" data-testid="dispatch-transport" />
              </div>
            </div>

            {/* Distributor Summary */}
            {pendingData && (
              <Card className="bg-indigo-50 border-indigo-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-indigo-900">{pendingData.distributor.company_name}</h3>
                      <p className="text-xs text-indigo-700">{pendingData.distributor.state} | Discount: {pendingData.distributor.discount_percentage || 0}% | Customers: {pendingData.customers.length}</p>
                    </div>
                    <div className="text-right text-sm">
                      <p className="text-indigo-700">Pending: <strong>{pendingData.summary.total_orders} orders</strong>, {pendingData.summary.total_qty} pcs</p>
                      <p className="text-indigo-700">Value: <strong>₹{pendingData.summary.total_value.toLocaleString('en-IN')}</strong></p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Orders Selection */}
            {pendingData && pendingData.orders.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold">Select Orders to Dispatch</Label>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={selectAllOrders} data-testid="dispatch-select-all">
                    {selectedOrders.length === pendingData.orders.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
                <div className="max-h-72 overflow-y-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 w-8"></th>
                        <th className="text-left px-3 py-2">SO #</th>
                        <th className="text-left px-3 py-2">Customer</th>
                        <th className="text-left px-3 py-2">City</th>
                        <th className="text-left px-3 py-2">Items</th>
                        <th className="text-right px-3 py-2">Qty</th>
                        <th className="text-right px-3 py-2">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingData.orders.map(o => (
                        <tr key={o.id} className={`border-t cursor-pointer hover:bg-indigo-50/50 ${selectedOrders.includes(o.id) ? 'bg-indigo-50' : ''}`} onClick={() => toggleOrder(o.id)} data-testid={`dispatch-order-${o.id}`}>
                          <td className="px-3 py-2 text-center">
                            <input type="checkbox" checked={selectedOrders.includes(o.id)} readOnly className="rounded" />
                          </td>
                          <td className="px-3 py-2 font-medium text-indigo-700">{o.so_number}</td>
                          <td className="px-3 py-2 font-medium">{o.customer_name}</td>
                          <td className="px-3 py-2 text-gray-500">{o.customer_city}</td>
                          <td className="px-3 py-2">
                            {(o.items || []).map((item, i) => <Badge key={i} variant="outline" className="text-[9px] mr-1">{item.design_code}</Badge>)}
                          </td>
                          <td className="px-3 py-2 text-right">{o.total_qty}</td>
                          <td className="px-3 py-2 text-right">₹{(o.total_amount || 0).toLocaleString('en-IN')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {selectedOrders.length > 0 && (
                  <Card className="bg-emerald-50 border-emerald-200">
                    <CardContent className="p-3">
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div><span className="text-gray-600">Orders:</span> <strong>{selectedOrders.length}</strong></div>
                        <div><span className="text-gray-600">Customers:</span> <strong>{summary.customers}</strong></div>
                        <div><span className="text-gray-600">Total Qty:</span> <strong>{summary.qty} pcs</strong></div>
                        <div><span className="text-gray-600">Gross Value:</span> <strong>₹{summary.value.toLocaleString('en-IN')}</strong></div>
                      </div>
                      {pendingData.distributor.discount_percentage > 0 && (
                        <div className="mt-2 text-sm text-right">
                          <span className="text-red-600">Discount ({pendingData.distributor.discount_percentage}%): -₹{Math.round(summary.value * pendingData.distributor.discount_percentage / 100).toLocaleString('en-IN')}</span>
                          <span className="ml-4 font-bold text-emerald-700">Net Invoice: ₹{Math.round(summary.value * (1 - pendingData.distributor.discount_percentage / 100)).toLocaleString('en-IN')}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {pendingData && pendingData.orders.length === 0 && (
              <div className="text-center py-8 text-gray-500">No pending orders for this distributor's customers.</div>
            )}

            {/* Notes + Submit */}
            {selectedOrders.length > 0 && (
              <div className="space-y-3">
                <Textarea value={dispatchNotes} onChange={e => setDispatchNotes(e.target.value)} placeholder="Dispatch notes..." className="h-16" data-testid="dispatch-notes" />
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => { resetForm(); setIsCreateOpen(false); }}>Cancel</Button>
                  <Button onClick={submitDispatch} className="bg-indigo-600 hover:bg-indigo-700" data-testid="dispatch-submit">
                    Create Dispatch ({selectedOrders.length} orders, {summary.customers} customers)
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* View Dispatch Dialog */}
      {viewingDispatch && (
        <Dialog open={!!viewingDispatch} onOpenChange={() => setViewingDispatch(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto z-50">
            <DialogHeader>
              <DialogTitle>{viewingDispatch.dispatch_number}</DialogTitle>
              <DialogDescription>
                {viewingDispatch.distributor_name} | {formatDate(viewingDispatch.dispatch_date)} | Transport: {viewingDispatch.transport_name || '-'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Invoice Summary */}
              <Card className="bg-gray-50">
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div><span className="text-gray-500">Customers</span><br/><strong>{viewingDispatch.total_customers}</strong></div>
                    <div><span className="text-gray-500">Total Qty</span><br/><strong>{viewingDispatch.total_qty} pcs</strong></div>
                    <div><span className="text-gray-500">Gross Value</span><br/><strong>₹{(viewingDispatch.gross_value || 0).toLocaleString('en-IN')}</strong></div>
                    <div><span className="text-gray-500">Discount</span><br/><strong className="text-red-600">-{viewingDispatch.discount_percentage}% (₹{(viewingDispatch.discount_amount || 0).toLocaleString('en-IN')})</strong></div>
                    <div><span className="text-gray-500">Net Invoice</span><br/><strong className="text-emerald-700 text-lg">₹{(viewingDispatch.net_value || 0).toLocaleString('en-IN')}</strong></div>
                  </div>
                </CardContent>
              </Card>

              {/* Packing Slips */}
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Customer-wise Packing Slips ({(viewingDispatch.packing_slips || []).length})</Label>
                <Button size="sm" variant="outline" className="text-xs text-violet-700 border-violet-300" onClick={() => printAllSlips(viewingDispatch)} data-testid="dispatch-print-all-slips">
                  Print All Slips
                </Button>
              </div>
              {(viewingDispatch.packing_slips || []).map((slip, i) => (
                <Card key={i} className="border-l-4 border-l-indigo-400">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="font-bold text-sm">{slip.customer_name}</span>
                        <span className="text-xs text-gray-500 ml-2">{slip.customer_city} | {slip.customer_phone}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{slip.total_qty} pcs — ₹{slip.total_value.toLocaleString('en-IN')}</span>
                        <Button size="sm" variant="ghost" className="h-6 text-xs text-violet-600" onClick={() => printPackingSlip(viewingDispatch, slip.customer_id)} data-testid={`slip-print-${i}`}>
                          Print
                        </Button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {slip.items.map((item, j) => (
                        <Badge key={j} variant="outline" className="text-[10px]">{item.design_code} {item.size}/{item.color} x{item.qty}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {viewingDispatch.notes && <p className="text-sm text-gray-600 italic">Notes: {viewingDispatch.notes}</p>}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default DistributorDispatch;
